import { css } from 'styled-components';

// Hero glass — thick, smooth, transparent optical glass feel
// Use this for the primary hero section container only.
export const heroGlassEffect = css`
  backdrop-filter: blur(48px) saturate(240%) brightness(1.08);
  -webkit-backdrop-filter: blur(48px) saturate(240%) brightness(1.08);
  border: 1.5px solid rgba(255, 255, 255, 0.38);
  border-top-color: rgba(255, 255, 255, 0.65);
  border-bottom-color: rgba(255, 255, 255, 0.18);
  box-shadow:
    /* Outer ambient depth */
    0 32px 96px rgba(0, 0, 0, 0.18),
    0 16px 48px rgba(0, 0, 0, 0.12),
    0 6px 18px rgba(0, 0, 0, 0.08),
    /* Thick-glass inner edge highlights */
    inset 0 3px 0 rgba(255, 255, 255, 0.72),
    inset 0 -2px 0 rgba(0, 0, 0, 0.08),
    inset 3px 0 0 rgba(255, 255, 255, 0.18),
    inset -3px 0 0 rgba(255, 255, 255, 0.10),
    /* Subtle inner glow for optical depth */
    inset 0 0 40px rgba(255, 255, 255, 0.06);
  position: relative;
  overflow: hidden;
  will-change: transform;
  transform: translateZ(0);

  /* Thick glass depth gradient — simulates light bending through glass volume */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    /*
      DWEB-179 — 160deg became 200deg so the panel is lit from ONE direction.

      A CSS gradient angle names where the line POINTS, so the bright 0% stop
      sits at the opposite end: 160deg put it top-LEFT. The page's ambient wash
      is radial-gradient(60% 55% at 80% 4%, ...) in DynamicSkyBackground.js —
      top-RIGHT. With ::after also throwing a highlight from directly above,
      the panel was lit from three directions at once and nothing reconciled
      them. ::after is gone (above); 200deg puts this one's bright end
      top-right, where the page's light actually is.

      The COLOURS are unchanged. Only the angle moved.
    */
    background: linear-gradient(
      200deg,
      rgba(255, 255, 255, 0.28) 0%,
      rgba(255, 255, 255, 0.10) 20%,
      rgba(255, 255, 255, 0.04) 45%,
      rgba(255, 255, 255, 0.02) 65%,
      rgba(0, 0, 0, 0.03) 80%,
      rgba(0, 0, 0, 0.07) 100%
    );
    pointer-events: none;
    z-index: 1;
    border-radius: inherit;
  }

  /*
    DWEB-179 — the specular sheen was DELETED here, not retuned.

    It was a 341px band (38% of the panel) of rgba(255,255,255,.38) fading
    down, with a 0 0 60% 60% / 0 0 30px 30px radius that gave it a curved
    bottom edge. That edge is what made it read as a STREAK rather than as a
    gradient, and the owner's report was "the washout streak on the hero".

    WHY IT LOOKED RIGHT ONCE AND WRONG NOW. It was tuned when the panel sat on
    the aurora sky. A 38% white band across the top of a pane is a plausible
    specular return FROM SOMETHING BRIGHT ABOVE IT. v6.7.0 (DWEB-170) deleted
    that sky for a #000000 ground, so there is nothing above the panel to
    return — and a highlight with no source reads as fog on the glass rather
    than as light on it. Sixth instance of this release arc's standing pattern:
    a value correct under the conditions it was tuned in, carried into
    conditions it was never measured against.

    WHY DELETION RATHER THAN A SHALLOWER, DIMMER BAND. The cheap diagnostic was
    run first and answered the question: with ::after simply switched off, the
    panel still reads as glass — the lit top rim (inset 0 3px 0 at .72 and the
    .65 top border) and ::before's depth gradient carry it between them, and
    the headline gains a great deal of contrast. Switching ::before off as well
    DOES flatten the panel, which is how ::before is known to be load-bearing
    and this was not. A dimmer band would have been a value nobody could
    justify, replacing one that at least had a reason once.
  */

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Hero glass hover — smooth lift with increased optical depth
export const heroHoverEffect = css`
  transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);

  &:hover {
    transform: translateY(-6px) translateZ(0);
    backdrop-filter: blur(56px) saturate(260%) brightness(1.10);
    -webkit-backdrop-filter: blur(56px) saturate(260%) brightness(1.10);
    border-color: rgba(255, 255, 255, 0.48);
    border-top-color: rgba(255, 255, 255, 0.75);
    box-shadow:
      0 48px 120px rgba(0, 0, 0, 0.22),
      0 24px 60px rgba(0, 0, 0, 0.14),
      0 8px 24px rgba(0, 0, 0, 0.10),
      inset 0 4px 0 rgba(255, 255, 255, 0.80),
      inset 0 -2px 0 rgba(0, 0, 0, 0.10),
      inset 3px 0 0 rgba(255, 255, 255, 0.22),
      inset -3px 0 0 rgba(255, 255, 255, 0.14),
      inset 0 0 60px rgba(255, 255, 255, 0.08);
  }

  /*
    DWEB-179 — the hover sheen went with the sheen it brightened.

    This rule set only a background on ::after. With ::after's content gone
    from heroGlassEffect the pseudo-element never generates, so the rule styles
    something that does not exist: inert, and indistinguishable from working
    code to the next reader. That is the same defect DWEB-180 removed from
    PageContainer in this release, so it is not left behind here.

    Hover still reads: the lift, the deeper blur, the brighter rim and the
    heavier shadow all remain in the block above.
  */
`;

// Common glass effect styles with enhanced depth
export const glassEffect = css`
  backdrop-filter: blur(25px) saturate(200%);
  -webkit-backdrop-filter: blur(25px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.12),
    0 8px 25px rgba(0, 0, 0, 0.08),
    0 2px 8px rgba(0, 0, 0, 0.04),
    inset 0 2px 0 rgba(255, 255, 255, 0.6),
    inset 0 -2px 0 rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  will-change: transform;
  transform: translateZ(0);
  
  /* Glass-like depth effect */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0.1) 25%,
      rgba(255, 255, 255, 0.05) 50%,
      rgba(0, 0, 0, 0.05) 75%,
      rgba(0, 0, 0, 0.1) 100%
    );
    pointer-events: none;
    z-index: 1;
  }
  
  /* Glass reflection effect */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 2;
  }
`;

// Enhanced hover effects with glass depth
export const hoverEffect = css`
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover {
    transform: translateY(-8px) translateZ(0) scale(1.02);
    box-shadow: 
      0 30px 80px rgba(0, 0, 0, 0.15),
      0 12px 40px rgba(0, 0, 0, 0.12),
      0 6px 20px rgba(0, 0, 0, 0.08),
      inset 0 3px 0 rgba(255, 255, 255, 0.7),
      inset 0 -3px 0 rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(30px) saturate(220%);
    -webkit-backdrop-filter: blur(30px) saturate(220%);
    border-color: rgba(255, 255, 255, 0.4);
  }
  
  &:hover::before {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.5) 0%,
      rgba(255, 255, 255, 0.2) 25%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(0, 0, 0, 0.03) 75%,
      rgba(0, 0, 0, 0.08) 100%
    );
  }
  
  &:hover::after {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0.15) 50%,
      transparent 100%
    );
  }
`;
